import React from "react";
import { makeStyles, createStyles, Theme, Button } from "@material-ui/core";

type DefaultStyle = {
    flexGrow: number;
    borderRadius: string;
    paddingTop: string;
    paddingRight: string;
    paddingBottom: string;
    paddingLeft: string;
    gap: string;
    boxShadow: string;
    textTransform: "unset";
    transition: string;
}

const defaultStyle: DefaultStyle = {
    flexGrow: 1,
    borderRadius: "var(--radius-s)",
    paddingTop: "var(--spacing-xs)",
    paddingRight: "var(--spacing-m)",
    paddingBottom: "var(--spacing-xs)",
    paddingLeft: "var(--spacing-m)",
    gap: "var(--spacing-xs)",
    boxShadow: "none",
    textTransform: "unset",
    transition: "none",
};

const primaryStyle = (theme: Theme) => ({
  ...defaultStyle,
  backgroundColor: "var(--col-surface-cta-primary-default)",
  width: theme.spacing(40),
  height: "40px",
  color: "var(--col-text-white)",

  "&:hover:not([disabled])": {
    boxShadow: "none",
    backgroundColor: "var(--col-surface-cta-primary-hover)",
    color: "var(--col-surface-cta-primary-text)",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "var(--col-surface-cta-primary-default)",
  },

  "&:active:not([disabled])": {
    boxShadow: "none",
    backgroundColor: "var(--col-surface-cta-primary-pressed)",
    border: "none",
    color: "var(--col-text-white)",
  },

  "&:focus-visible": {
    outline: "none",
  },

  "&[disabled]": {
    cursor: "default",
    backgroundColor: "var(--col-surface-cta-primary-disabled)",
    color: "var(--col-text-disabled)",
    border: "none",
  },

  [theme.breakpoints.only("xs")]: {
    width:"100%",
  },
});

const secondaryStyle = (theme: Theme) => ({
  ...defaultStyle,
  backgroundColor: "var(--col-surface-generic-white)",
  width:theme.spacing(40),
  height: "40px",
  color: "var(--col-text-primary)",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "var(--col-border-default)",

  "&:hover:not([disabled])": {
    boxShadow: "none",
    backgroundColor: "var(--col-surface-cta-secondary-hover)",
    color: "var(--col-text-primary)",
    borderColor: "var(--col-border-default)",
  },

  "&:active:not([disabled])": {
    boxShadow: "none",
    backgroundColor: "var(--col-surface-cta-secondary-pressed)",
    borderColor: "var(--col-border-default)",
    color: "var(--col-text-primary)",
  },

  "&:focus-visible": {
    outline: "none",
  },

  "&[disabled]": {
    cursor: "default",
    backgroundColor: "var(--col-surface-cta-primary-disabled)",
    color: "var(--col-text-disabled)",
    border: "none",
  },

  [theme.breakpoints.only("xs")]: {
    width:"100%",
  },
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Pink / Primary Large
    buttonPink: {
      ...primaryStyle(theme),
      marginTop: theme.spacing(1),
      height: "56px",
    },

    // Large White / Secondary Large
    buttonLargeWhite: {
      ...secondaryStyle(theme),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      height: "56px",
    },

    // White / Secondary
    buttonWhite: {
      ...secondaryStyle(theme),
      marginTop: theme.spacing(1),
      width: "280px",
      height: "40px",
    },

    // Label
    buttonLabel: {
      lineHeight: "1.5",
      fontSize: "16px",
      fontWeight: "normal",
      color: "inherit"
    },
  })
);
export enum BwtButtons {
  white,
  pink,
  largewhite,
}
interface BwtButtonProps {
  label: string;
  disabled?:boolean;
  type?: "button" | "reset" | "submit" | undefined;
  onClick?: () => void;
  styleType?: BwtButtons;
}

export const BwtButton: React.FC<BwtButtonProps> = ({
  label,
  styleType = BwtButtons.white,
  onClick,
  type,
  disabled
}) => {
  const classes = useStyles();

  return (
    <React.Fragment>
      {styleType === BwtButtons.white && (
        <Button
          type={type}
          variant="contained"
          size="small"
          color="default"
          className={classes.buttonWhite}
          classes={{
            label: classes.buttonLabel,
          }}
          disabled={disabled}
          onClick={onClick}
          disableRipple
        >
          {label}
        </Button>
      )}
      {styleType === BwtButtons.pink && (
        <Button
          type={type}
          variant="contained"
          size="large"
          color="secondary"
          className={classes.buttonPink}
          classes={{
            label: classes.buttonLabel,
          }}
          disabled={disabled}
          onClick={onClick}
          disableRipple
        >
          {label}
        </Button>
      )}
      {styleType === BwtButtons.largewhite && (
        <Button
          type={type}
          variant="contained"
          size="large"
          color="secondary"
          className={classes.buttonLargeWhite}
          classes={{
              label: classes.buttonLabel,
          }}
          disabled={disabled}
          onClick={onClick}
          disableRipple
        >
          {label}
        </Button>
      )}
    </React.Fragment>
  );
};
